import { scaleOrdinal, scaleThreshold } from "d3";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useElapsedTime } from "use-elapsed-time";

import { PlayStateApi } from "../../../hooks";
import { Frame, SportPlay } from "../../../types";
import { getAllMovementActions } from "../../../utils";
import { getFrameMaxEndTime, getTotalPlayDuration } from "../utils";

export enum TIMELINE_VIEW {
  FRAME = "FRAME",
  ALL = "ALL",
}

export interface UseTimerProps {
  play: SportPlay;
  activeFrame: Frame | null;
  setActiveFrameId: PlayStateApi["setActiveFrameId"];
  startTime: number;
}

export interface FrameEndTime {
  frameId: string;
  endTime: number;
}

export const useAnimationTimerApi = ({ play, activeFrame, setActiveFrameId, startTime }: UseTimerProps) => {
  const [timelineView, setTimelineView] = useState<TIMELINE_VIEW>(TIMELINE_VIEW.FRAME);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const activeFrameStencils = useMemo(() => activeFrame?.frameStencils || [], [activeFrame]);

  /** Max Time */
  const maxTimeActiveFrame = useMemo(() => {
    const movementActions = getAllMovementActions(activeFrameStencils);
    const maxTime = Math.max(...movementActions.map((action) => action.endTime));
    return maxTime;
  }, [activeFrameStencils]);

  const maxTimeAllFrames = useMemo(
    () => (timelineView === TIMELINE_VIEW.FRAME ? maxTimeActiveFrame : getTotalPlayDuration(play)),
    [timelineView, maxTimeActiveFrame, play]
  );

  const [maxTime, setMaxTime] = useState<number>(maxTimeAllFrames);
  useEffect(() => {
    setMaxTime(maxTimeAllFrames);
  }, [maxTimeAllFrames]);

  /** Total Time */
  const startTimeSeconds = useMemo(() => startTime / 1000, [startTime]);
  const maxTimeSeconds = useMemo(() => maxTime / 1000, [maxTime]);
  const { elapsedTime: elapsedTimeSeconds, reset: setTimeSeconds } = useElapsedTime({
    isPlaying,
    startAt: startTimeSeconds,
    duration: maxTimeSeconds - startTimeSeconds,
  });
  const time = useMemo(() => elapsedTimeSeconds * 1000, [elapsedTimeSeconds]);
  const setTime = useCallback(
    (newTime: number) => {
      setTimeSeconds(newTime / 1000);
    },
    [setTimeSeconds]
  );

  /** Time of Active Frame */
  const effectiveTime = useMemo(() => {
    if (timelineView === TIMELINE_VIEW.FRAME) return time;

    const activeFrameIndex = play.frames.findIndex((frame) => frame.id === activeFrame?.id);
    const timeUntilFrame = activeFrame
      ? play.frames.slice(0, activeFrameIndex).reduce((acc, frame) => {
          const frameMarkers = frame.frameStencils || [];

          const movementActions = getAllMovementActions(frameMarkers);

          // Math.max should not return a negative number
          return acc + Math.max(...movementActions.map((action) => action.endTime), 0);
        }, 0)
      : 0;
    return time - timeUntilFrame;
  }, [timelineView, activeFrame, play, time]);

  /** Update Active Frame by Time */
  const { timeToFrameScale, frameToStartTimeScale } = useMemo(() => {
    const frameEndTimes: FrameEndTime[] = play.frames
      .map((frame) => {
        return { endTime: getFrameMaxEndTime(frame), frameId: frame.id };
      })
      .map((f) => ({ ...f, endTime: Math.max(f.endTime, 12.5) }))
      .reduce<FrameEndTime[]>(
        (acc, { endTime, frameId }) => [...acc, { endTime: (acc[acc.length - 1]?.endTime || 0) + endTime, frameId }],
        []
      );
    const timeToFrameScale = scaleThreshold<number, string>()
      .domain(frameEndTimes.map((f) => f.endTime))
      .range(frameEndTimes.map((f) => f.frameId));

    const frameToStartTimeScale = scaleOrdinal<string, number>()
      .domain(frameEndTimes.map((f) => f.frameId))
      .range(frameEndTimes.map((f, i, arr) => arr[i - 1]?.endTime || 0));

    return { timeToFrameScale, frameToStartTimeScale };
  }, [play]);

  useEffect(() => {
    if (activeFrame && timelineView === TIMELINE_VIEW.ALL) {
      // Determine correct frame given current time.  Update active frame if necessary.
      // if new frameFromTime has no frameStencils, do not update active frame
      const frameFromTime = timeToFrameScale(time);
      const frame = play.frames.find((f) => f.id === frameFromTime);
      if (frameFromTime !== activeFrame.id && frame?.frameStencils?.length) {
        setActiveFrameId(frameFromTime);
      }
    }
  }, [activeFrame, timelineView, timeToFrameScale, time, setActiveFrameId, play.frames]);

  const playTimer = () => setIsPlaying(true);
  const pauseTimer = () => setIsPlaying(false);
  const resetTimer = () => setTime(startTime);

  useEffect(() => {
    if (time >= maxTime) pauseTimer();
  }, [time, maxTime]);

  return {
    time,
    setTime,
    effectiveTime,
    timelineView,
    frameToStartTimeScale,
    setTimelineView,
    isPlaying,
    playTimer,
    pauseTimer,
    resetTimer,
    maxTime,
    setMaxTime,
  };
};

export type AnimationTimerApi = ReturnType<typeof useAnimationTimerApi>;

export const DEFAULT_ANIMATION_TIMER_API: AnimationTimerApi = {
  time: 0,
  setTime: () => undefined,
  effectiveTime: 0,
  timelineView: TIMELINE_VIEW.FRAME,
  frameToStartTimeScale: scaleOrdinal<string, number>(),
  setTimelineView: () => undefined,
  isPlaying: false,
  playTimer: () => undefined,
  pauseTimer: () => undefined,
  resetTimer: () => undefined,
  maxTime: 0,
  setMaxTime: () => undefined,
};
